import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import useMobileModalMode from '../../hooks/useMobileModalMode';
import api from '../../utils/api';
import { getLocalDateInputValue, toDateInputValue } from '../../utils/dateUtils';
import { CURRENCIES } from '../../utils/personnelLabels';
import toast from 'react-hot-toast';

const emptyForm = () => ({
    fullName: '',
    position: '',
    phone: '',
    address: '',
    emergencyName: '',
    emergencyPhone: '',
    emergencyRelation: '',
    hireDate: getLocalDateInputValue(),
    weeklyWage: '',
    wageCurrency: 'TRY',
    annualLeaveEntitlement: 14,
    notes: '',
});

const PersonnelModal = ({ isOpen, onClose, onRefresh, editData = null }) => {
    const [formData, setFormData] = useState(emptyForm());
    const [isSubmitting, setIsSubmitting] = useState(false);

    useMobileModalMode(isOpen);

    useEffect(() => {
        if (!isOpen) return;
        if (editData) {
            setFormData({
                fullName: editData.fullName || '',
                position: editData.position || '',
                phone: editData.phone || '',
                address: editData.address || '',
                emergencyName: editData.emergencyName || '',
                emergencyPhone: editData.emergencyPhone || '',
                emergencyRelation: editData.emergencyRelation || '',
                hireDate: editData.hireDate ? toDateInputValue(editData.hireDate) : '',
                weeklyWage: editData.weeklyWage ?? '',
                wageCurrency: editData.wageCurrency || 'TRY',
                annualLeaveEntitlement: editData.annualLeaveEntitlement ?? 14,
                notes: editData.notes || '',
            });
        } else {
            setFormData(emptyForm());
        }
    }, [editData, isOpen]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.fullName.trim()) {
            toast.error('Ad Soyad zorunludur.');
            return;
        }
        setIsSubmitting(true);
        try {
            const payload = {
                ...formData,
                weeklyWage: parseFloat(formData.weeklyWage || 0),
                annualLeaveEntitlement: parseInt(formData.annualLeaveEntitlement, 10) || 0,
            };
            if (editData && editData.id) {
                await api.put(`/personnel/${editData.id}`, payload);
                toast.success('Personel güncellendi.');
            } else {
                await api.post('/personnel', payload);
                toast.success('Personel eklendi.');
            }
            onRefresh?.({ resetToFirstPage: !editData });
            onClose();
        } catch (error) {
            console.error('Personel kaydı başarısız', error);
            toast.error(error.response?.data?.message || 'Kayıt işlemi başarısız.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputClass = 'w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg focus:outline-none focus:border-primary-500 text-white';
    const labelClass = 'block text-sm font-medium text-slate-300 mb-1';

    return (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-900/80 backdrop-blur-sm sm:items-center sm:p-4">
            <div className="bg-dark-card border border-slate-700 w-full max-w-2xl text-white rounded-t-2xl shadow-2xl overflow-hidden flex flex-col max-h-[calc(100dvh-0.5rem)] sm:max-h-[90vh] sm:rounded-2xl">

                <div className="flex justify-between items-center p-5 sm:p-6 border-b border-slate-700/50 bg-slate-800/30">
                    <h2 className="text-xl font-bold">{editData ? 'Personel Düzenle' : 'Yeni Personel'}</h2>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-full transition-colors shrink-0">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-5 sm:p-6 overflow-y-auto custom-scrollbar">
                    <form id="personnelForm" onSubmit={handleSubmit} className="space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className={labelClass}>Ad Soyad <span className="text-red-400">*</span></label>
                                <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} className={inputClass} placeholder="Örn: Ahmet Yılmaz" />
                            </div>
                            <div>
                                <label className={labelClass}>Görev / Pozisyon</label>
                                <input type="text" name="position" value={formData.position} onChange={handleChange} className={inputClass} placeholder="Örn: Kalıpçı" />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className={labelClass}>Telefon</label>
                                <input type="text" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} placeholder="05xx xxx xx xx" />
                            </div>
                            <div>
                                <label className={labelClass}>İşe Giriş Tarihi</label>
                                <input type="date" name="hireDate" value={formData.hireDate} onChange={handleChange} className={`${inputClass} cursor-pointer`} />
                            </div>
                        </div>

                        <div>
                            <label className={labelClass}>Adres</label>
                            <textarea name="address" rows="2" value={formData.address} onChange={handleChange} className={inputClass} placeholder="Ev adresi" />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            <div>
                                <label className={labelClass}>Haftalık Ücret</label>
                                <input type="number" step="0.01" name="weeklyWage" value={formData.weeklyWage} onChange={handleChange} className={inputClass} placeholder="0.00" />
                            </div>
                            <div>
                                <label className={labelClass}>Para Birimi</label>
                                <select name="wageCurrency" value={formData.wageCurrency} onChange={handleChange} className={inputClass}>
                                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={labelClass}>Yıllık İzin Hakkı (gün)</label>
                                <input type="number" step="1" name="annualLeaveEntitlement" value={formData.annualLeaveEntitlement} onChange={handleChange} className={inputClass} placeholder="14" />
                            </div>
                        </div>

                        <div className="border-t border-slate-700/50 pt-4">
                            <p className="text-sm font-semibold text-slate-300 mb-3">Acil Durum İletişim</p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                <div>
                                    <label className={labelClass}>Kişi Adı</label>
                                    <input type="text" name="emergencyName" value={formData.emergencyName} onChange={handleChange} className={inputClass} placeholder="Yakınının adı" />
                                </div>
                                <div>
                                    <label className={labelClass}>Telefon</label>
                                    <input type="text" name="emergencyPhone" value={formData.emergencyPhone} onChange={handleChange} className={inputClass} placeholder="05xx xxx xx xx" />
                                </div>
                                <div>
                                    <label className={labelClass}>Yakınlık</label>
                                    <input type="text" name="emergencyRelation" value={formData.emergencyRelation} onChange={handleChange} className={inputClass} placeholder="Örn: Eş, Kardeş" />
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className={labelClass}>Notlar</label>
                            <textarea name="notes" rows="3" value={formData.notes} onChange={handleChange} className={inputClass} placeholder="Özel notlar" />
                        </div>
                    </form>
                </div>

                <div className="sticky bottom-0 z-10 p-4 sm:p-6 border-t border-slate-700/50 bg-slate-800/95 flex justify-end gap-3 pb-safe-offset">
                    <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-lg font-medium text-slate-300 hover:bg-slate-700 transition-colors">
                        Vazgeç
                    </button>
                    <button
                        type="submit"
                        form="personnelForm"
                        disabled={isSubmitting}
                        className="px-6 py-2.5 rounded-lg font-bold text-white bg-primary-600 hover:bg-primary-500 shadow-lg shadow-primary-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                        {isSubmitting ? 'Kaydediliyor...' : (editData ? 'Güncelle' : 'Kaydet')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PersonnelModal;
